import { ReactNode } from "react";
import * as Sentry from "@sentry/nextjs";
import { Alert, AlertTitle, AlertDescription } from "@repo/ui/components/ui/alert";
import { Button } from "@repo/ui/components/ui/button";
import { Body } from "@repo/ui/components/ui/text";
type ErrorBoundaryProps = Sentry.ErrorBoundaryProps & {
  showErrorMessage?: boolean;
};

/**
 * ErrorBoundary component that wraps Sentry's ErrorBoundary,
 * logs errors to Sentry, and displays a fallback UI.
 */
export function ErrorBoundary({
  children,
  fallback,
  showErrorMessage,
  ...props
}: ErrorBoundaryProps) {
  return <Sentry.ErrorBoundary {...props} fallback={({
    error,
    componentStack,
    resetError
  }) => {
    // If a custom fallback is provided, use it
    if (fallback) {
      return fallback as React.ReactElement;
    }
    const errorMessage = error ? typeof error === "string" ? error : error instanceof Error ? error.message : "An unknown error occurred" : "An unknown error occurred";
    return <Alert variant="destructive">
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription>
              <p className="mb-4">
                We've been notified about this issue and are working to fix it.
              </p>
              {showErrorMessage && errorMessage ? <Body>{errorMessage}</Body> : null}

              <Button variant="fill" intent="danger" onClick={resetError}>
                Try again
              </Button>
            </AlertDescription>
          </Alert>;
  }} data-sentry-element="unknown" data-sentry-component="ErrorBoundary" data-sentry-source-file="error-boundary.tsx">
      {children}
    </Sentry.ErrorBoundary>;
}

/**
 * Higher-order component that wraps a component with an ErrorBoundary
 */
export function withErrorBoundary<P extends object>(Component: React.ComponentType<P>, fallback?: ErrorBoundaryProps["fallback"]): React.FC<P> {
  const displayName = Component.displayName || Component.name || "Component";
  const WrappedComponent: React.FC<P> = props => {
    return <ErrorBoundary fallback={fallback} data-sentry-element="ErrorBoundary" data-sentry-component="WrappedComponent" data-sentry-source-file="error-boundary.tsx">
        <Component {...props} data-sentry-element="Component" data-sentry-source-file="error-boundary.tsx" />
      </ErrorBoundary>;
  };
  WrappedComponent.displayName = `withErrorBoundary(${displayName})`;
  return WrappedComponent;
}